import React, { useEffect, useState } from 'react'
import { useRecordContext, useNotify } from 'react-admin'
import { Box, CircularProgress, Alert, Typography, Collapse, IconButton } from '@mui/material'
import { ExpandMore, ExpandLess } from '@mui/icons-material'

import { CompanyRecord } from 'api/company'
import { Subscription, SubscriptionStatus, getCompanySubscription, getSubscriptionHistory } from 'api/subscriptions'
import RoundedPanel, { Label } from 'common/RoundedPanel'
import ActionButton from 'components/ActionButton'
import { handleEsc } from 'common/closeOnEscape'
import { CreateSubscriptionModal, UpdateSubscriptionModal } from './companyModals/index'
import { parseTimestamp } from 'utils/helpers'
import UserService from 'UserService'

type CurrentModal = "CREATE_SUBSCRIPTION" | "UPDATE_SUBSCRIPTION"

const Subscriptions = () => {
  const record = useRecordContext<CompanyRecord>()
  const [subscription, setSubscription] = useState<Subscription | null | undefined>(undefined)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [open, setOpen] = useState<CurrentModal | null>(null)
  const notify = useNotify()

  const closeModal = () => { 
    setOpen(null) 
  }

  React.useEffect(handleEsc(closeModal), [])

  const fetchSubscriptions = () => {
    if (!record?.id) return

    setIsLoading(true)
    setError(null)

    getCompanySubscription(record.id.toString())
      .then((subscription) => {
        setSubscription(subscription)
        setIsLoading(false)
      })
      .catch((err) => {
        console.error('Error fetching subscriptions:', err)
        setError('Failed to load subscription information')
        setIsLoading(false)
        notify('Failed to load subscription', { type: 'warning' })
      })
  }

  useEffect(() => {
    fetchSubscriptions()
  }, [record?.id, notify])

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="200px">
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box margin="60px 0px">
        <Alert severity="error">{error}</Alert>
      </Box>
    )
  }

  return (
    <>
      <CreateSubscriptionModal
        record={record}
        open={open === "CREATE_SUBSCRIPTION"}
        onClose={closeModal}
        onSuccess={fetchSubscriptions}
      />
      
      {subscription && (
        <UpdateSubscriptionModal
          record={record}
          subscription={subscription}
          open={open === "UPDATE_SUBSCRIPTION"}
          onClose={closeModal}
          onSuccess={fetchSubscriptions}
        />
      )}
      
      <Box className="edit-tab">
        <Box className='on-left'>
          {subscription === null ? (
            <ActionButton
              onClick={() => setOpen("CREATE_SUBSCRIPTION")}
            >
              Create Subscription
            </ActionButton>
          ) : (
            <ActionButton
              onClick={() => setOpen("UPDATE_SUBSCRIPTION")}
            >
              Update Subscription
            </ActionButton>
          )}
        </Box>
      </Box>

      <Box className="panel">
        <Box>
          {subscription === null ? (
            <RoundedPanel width="600px">
              <Label>No Subscription</Label>
              <hr />
              <Typography variant="body1" color="text.secondary">
                This company does not have an active subscription.
              </Typography>
            </RoundedPanel>
          ) : subscription ? (
            <SubscriptionDetails subscription={subscription} />
          ) : null}
        </Box>
      </Box>
    </>
  )
}

const SubscriptionDetails = ({ subscription }: { subscription: Subscription }) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false)
  const [history, setHistory] = useState<Subscription[]>([])
  const [isLoadingHistory, setIsLoadingHistory] = useState<boolean>(false)
  const notify = useNotify()
  const { timezone } = UserService.getTokenUserInfo()

  const loadHistory = async () => {
    if (history.length > 0) return // Already loaded

    setIsLoadingHistory(true)
    try {
      const historyData = await getSubscriptionHistory(subscription.companyId)
      setHistory(historyData)
    } catch (error) {
      console.error('Error loading subscription history:', error)
      notify('Failed to load subscription history', { type: 'warning' })
    } finally {
      setIsLoadingHistory(false)
    }
  }

  const handleToggleExpanded = () => {
    setIsExpanded(!isExpanded)
    if (!isExpanded) {
      loadHistory()
    }
  }

  const getStatusColor = (status: SubscriptionStatus) => {
    switch (status) {
      case SubscriptionStatus.ACTIVE:
        return '#4caf50'
      case SubscriptionStatus.INACTIVE:
        return '#f44336'
      case SubscriptionStatus.TRIALING:
        return '#ff9800'
      default:
        return '#666'
    }
  }

  return (
    <RoundedPanel width="600px">
      <Label>Subscription Details</Label>
      <hr />
      
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <Label noSelect>Status: </Label>
        <Box 
          component="span" 
          sx={{ 
            color: getStatusColor(subscription.status),
            fontWeight: 'bold'
          }}
        >
          {subscription.status}
        </Box>
      </Box>
      
      <Label noSelect>Start Date: </Label>{subscription.startDate ? parseTimestamp(subscription.startDate, timezone, true) : 'N/A'}<br />
      <Label noSelect>End Date: </Label>{subscription.endDate ? parseTimestamp(subscription.endDate, timezone, true) : 'Endless'}<br />
      <Label noSelect>Automatically renew: </Label>{!subscription.cancelAtPeriodEnd ? 'true' : 'false'}<br />
      <Label noSelect>Version: </Label>{subscription.version}<br />
      <Label noSelect>Created: </Label>{subscription.createdAt ? parseTimestamp(subscription.createdAt, timezone, true) : 'N/A'}<br />
      <Label noSelect>Created By: </Label>{subscription.createdByUsername}<br />
      <Label noSelect>ID: </Label>{subscription.companyId}<br />

      <Box mt={2}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Label>Subscription History</Label>
          <IconButton onClick={handleToggleExpanded} size="small">
            {isExpanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>

        <Collapse in={isExpanded}>
          <hr />
          {isLoadingHistory ? (
            <Box display="flex" justifyContent="center" p={2}>
              <CircularProgress size={24} />
            </Box>
          ) : history.length > 0 ? (
            <Box>
              {history.map((version) => (
                <Box key={`${version.companyId}-${version.version}`} mb={2}>
                  <Typography variant="body2">
                    <strong>Version {version.version}</strong> - {parseTimestamp(version.createdAt, timezone, true)} by {version.createdByUsername}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Status: {version.status} | Start: {version.startDate ? parseTimestamp(version.startDate, timezone, true) : 'N/A'} | End: {version.endDate ? parseTimestamp(version.endDate, timezone, true) : 'Endless'} | Auto-renew: {!version.cancelAtPeriodEnd ? 'true' : 'false'}
                  </Typography>
                  {version.version < subscription.version && (
                    <hr style={{ margin: '8px 0', width: '50%', border: 'none', borderTop: '1px solid #ddd' }} />
                  )}
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No history available
            </Typography>
          )}
        </Collapse>
      </Box>
    </RoundedPanel>
  )
}

export default Subscriptions 